import { useTranslation } from 'react-i18next'
import Papa from 'papaparse'

import { BulkRejectConfirmView } from './BulkRejectConfirmView'
import { BulkRejectExceptionsTable } from './BulkRejectExceptionsTable'
import { BulkRejectRowErrorsTable } from './BulkRejectRowErrorsTable'
import { BulkRejectUploadView } from './BulkRejectUploadView'
import { downloadCsv } from './downloadCsv'
import { useAgentVerificationBulkReject } from './useAgentVerificationBulkReject'
import { AcknowledgeModal, ActionModal, Alert, ButtonText, Modal, Spacer, Text } from '@percent/lemonade'

const MODAL_WIDTH_STANDARD = 560
const MODAL_WIDTH_WIDE = 860
const EXCEPTIONS_CSV_FILENAME = 'bulk-reject-exceptions.csv'

type BulkRejectErrorMessageKey =
  | 'dialog.avBulkReject.error.missingRequiredCsvHeaders'
  | 'dialog.avBulkReject.error.maxCsvRowsExceeded'
  | 'dialog.avBulkReject.error.emptyCsvFile'
  | 'dialog.avBulkReject.error.invalidCsvFile'
  | 'dialog.avBulkReject.error.invalidRecords'
  | 'dialog.avBulkReject.error.unknown'

const UNKNOWN_ERROR_MESSAGE_KEY: BulkRejectErrorMessageKey = 'dialog.avBulkReject.error.unknown'

const ERROR_MESSAGE_KEY_BY_CODE: Record<string, BulkRejectErrorMessageKey> = {
  'agent_verification/bulk_reject/missing_required_csv_headers': 'dialog.avBulkReject.error.missingRequiredCsvHeaders',
  'agent_verification/bulk_reject/max_csv_rows_exceeded': 'dialog.avBulkReject.error.maxCsvRowsExceeded',
  'agent_verification/bulk_reject/empty_csv_file': 'dialog.avBulkReject.error.emptyCsvFile',
  'agent_verification/bulk_reject/invalid_csv_file': 'dialog.avBulkReject.error.invalidCsvFile',
  'agent_verification/bulk_reject/invalid_records': 'dialog.avBulkReject.error.invalidRecords'
}

export function AgentVerificationBulkRejectModal({ onClose }: Readonly<{ onClose: () => void }>) {
  const { t } = useTranslation()
  const {
    view,
    selectedFiles,
    parsed,
    rowCount,
    hasMissingHeaders,
    duplicateIds,
    canReview,
    canSubmit,
    batchId,
    queuedCount,
    errorCode,
    rowErrors,
    rejectedCount,
    checkedCount,
    exceptions,
    checkFailedCount,
    close,
    selectFiles,
    captureFileInputChange,
    captureDrop,
    review,
    submit,
    verify
  } = useAgentVerificationBulkReject({ onClose })

  const downloadExceptions = () => {
    downloadCsv(
      Papa.unparse({
        fields: ['agentVerificationId', 'status'],
        data: exceptions.map(({ agentVerificationId, outcome }) => [agentVerificationId, outcome])
      }),
      EXCEPTIONS_CSV_FILENAME
    )
  }

  const renderSubmittedView = () => (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.avBulkReject.submitted.title')}
      buttonText={t('dialog.avBulkReject.submitted.verify')}
      handleClose={verify}
      secondaryButtonText={t('button.close')}
      onSecondaryButtonClick={close}
    >
      <Text size="small" align="center">
        {t('dialog.avBulkReject.submitted.description', { count: queuedCount })}
      </Text>
      <Spacer size={2} />
      <Text size="small" align="center">
        {t('dialog.avBulkReject.submitted.batchId', { batchId })}
      </Text>
    </AcknowledgeModal>
  )

  const renderVerifyingView = () => (
    <ActionModal
      title={t('dialog.avBulkReject.verifying.title')}
      primaryButtonText={t('dialog.avBulkReject.verifying.inProgress')}
      secondaryButtonText={t('button.close')}
      handleClose={close}
      handleSubmit={verify}
      loading
      disabled
    >
      <Text size="small">
        {t('dialog.avBulkReject.verifying.progress', { checked: checkedCount, total: rowCount })}
      </Text>
    </ActionModal>
  )

  const renderVerifiedView = () => {
    const notRejectedCount = exceptions.length - checkFailedCount

    // Joined into one string rather than sibling nodes so the sentences cannot lose the space
    // between them.
    const description = [
      notRejectedCount > 0 && t('dialog.avBulkReject.verified.exceptionsDescription', { count: notRejectedCount }),
      checkFailedCount > 0 && t('dialog.avBulkReject.verified.checkFailedDescription', { count: checkFailedCount }),
      exceptions.length === 0 && t('dialog.avBulkReject.verified.allRejected')
    ]
      .filter(Boolean)
      .join(' ')

    // Rows we could not read back say nothing about the rejection, so a result made up purely of
    // those stays neutral instead of implying the rejections themselves failed.
    const getAlertVariant = () => {
      if (notRejectedCount > 0) return 'warning'

      return checkFailedCount > 0 ? 'default' : 'info'
    }

    return (
      <ActionModal
        title={t('dialog.avBulkReject.verified.title')}
        primaryButtonText={t('button.close')}
        handleClose={close}
        handleSubmit={close}
      >
        <Alert
          variant={getAlertVariant()}
          title={t('dialog.avBulkReject.verified.rejectedCount', { count: rejectedCount })}
        >
          {description}
        </Alert>
        {exceptions.length > 0 && (
          <>
            <Spacer size={4} />
            <BulkRejectExceptionsTable exceptions={exceptions} />
            <Spacer size={2} />
            <ButtonText variant="primary" onClick={downloadExceptions}>
              {t('dialog.avBulkReject.verified.downloadExceptions')}
            </ButtonText>
          </>
        )}
      </ActionModal>
    )
  }

  const renderErrorView = () => (
    <ActionModal
      title={t('dialog.avBulkReject.error.title')}
      primaryButtonText={t('button.close')}
      handleClose={close}
      handleSubmit={close}
    >
      <Alert variant="error" title={t('dialog.avBulkReject.error.nothingRejected')}>
        {t((errorCode && ERROR_MESSAGE_KEY_BY_CODE[errorCode]) || UNKNOWN_ERROR_MESSAGE_KEY)}
      </Alert>
      {rowErrors.length > 0 && (
        <>
          <Spacer size={4} />
          <BulkRejectRowErrorsTable rowErrors={rowErrors} />
        </>
      )}
    </ActionModal>
  )

  const isWideView = view === 'verified' || (view === 'error' && rowErrors.length > 0)

  return (
    <Modal open onClose={close} width={isWideView ? MODAL_WIDTH_WIDE : MODAL_WIDTH_STANDARD}>
      {view === 'upload' && (
        <BulkRejectUploadView
          selectedFiles={selectedFiles}
          canReview={canReview}
          onSelectFiles={selectFiles}
          onFileInputChange={captureFileInputChange}
          onDrop={captureDrop}
          onClose={close}
          onReview={review}
        />
      )}
      {(view === 'confirm' || view === 'submitting') && (
        <BulkRejectConfirmView
          rowCount={rowCount}
          canSubmit={canSubmit}
          hasMissingHeaders={hasMissingHeaders}
          missingHeaders={parsed?.missingHeaders ?? []}
          duplicateCount={duplicateIds.length}
          isSubmitting={view === 'submitting'}
          onClose={close}
          onSubmit={submit}
        />
      )}
      {view === 'submitted' && renderSubmittedView()}
      {view === 'verifying' && renderVerifyingView()}
      {view === 'verified' && renderVerifiedView()}
      {view === 'error' && renderErrorView()}
    </Modal>
  )
}
